import { useState, useEffect, useRef } from 'react';
import { pointAPI } from '../api/client';
import { useToastStore } from '../store/toastStore';
import BottomSheetSelect from './BottomSheetSelect';
import { track } from '../utils/analytics';

export default function PointChargeModal({ isOpen, onClose, userId, onChargeComplete }) {
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);
  const { showToast } = useToastStore();

  // Android 뒤로가기 버튼으로 모달 닫기
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    history.pushState({ modal: 'charge' }, '');

    const handlePopState = () => {
      onCloseRef.current();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen]);

  // X버튼/배경 클릭 시 history.back()으로 통일 (popstate -> onClose 호출)
  const handleClose = () => {
    history.back();
  };

  const handleMoonClick = (e) => {
    const img = e.target;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const pixel = ctx.getImageData(x * scaleX, y * scaleY, 1, 1).data;

    if (pixel[3] > 10) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      } else {
        audioRef.current = new Audio('/sounds/moon.mp3');
      }
      audioRef.current.play();
      track('hanwha-moon-song');
    }
  };
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [bankList, setBankList] = useState({});
  const [chargeOptions, setChargeOptions] = useState({});
  const [remainCash, setRemainCash] = useState('0');
  const [already, setAlready] = useState(null);
  const [selectedBank, setSelectedBank] = useState(() => localStorage.getItem('lastChargeBank') || '');
  const [selectedAmount, setSelectedAmount] = useState(() => localStorage.getItem('lastChargeAmount') || '');

  useEffect(() => {
    if (isOpen && userId) {
      fetchChargeList();
    }
  }, [isOpen, userId]);

  // 가상계좌 발급 대기 폴링 (5초 간격)
  useEffect(() => {
    if (!isOpen || !already || already.payAddr) return;

    const interval = setInterval(async () => {
      try {
        const res = await pointAPI.getChargeList(userId);
        if (res.success) {
          const updated = res.data.already || null;
          setAlready(updated);
          setRemainCash(res.data.remainCash || '0');
          if (updated?.payAddr) {
            clearInterval(interval);
            onChargeComplete?.();
          }
        }
      } catch {}
    }, 5000);

    return () => clearInterval(interval);
  }, [isOpen, already?.paySeq, already?.payAddr, userId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedBank(localStorage.getItem('lastChargeBank') || '');
      setSelectedAmount(localStorage.getItem('lastChargeAmount') || '');
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [isOpen]);

  const fetchChargeList = async () => {
    setLoading(true);
    try {
      const res = await pointAPI.getChargeList(userId);
      if (res.success) {
        const banks = res.data.virtBankList || {};
        delete banks['90']; // 카카오뱅크 제외 (가상계좌 발급 불가)
        setBankList(banks);
        setChargeOptions(res.data.cashList || {});
        setRemainCash(res.data.remainCash || '0');
        setAlready(res.data.already || null);
      }
    } catch (err) {
      console.error('[PointCharge] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCharge = async () => {
    if (!selectedBank || !selectedAmount) return;

    const bankName = bankList[selectedBank];
    const amount = Number(selectedAmount);

    if (!confirm(
      `포인트 충전을 신청하시겠습니까?\n\n` +
      `은행: ${bankName}\n` +
      `충전 포인트: ${amount.toLocaleString()}P\n` +
      `입금 금액: ${amount.toLocaleString()}원`
    )) return;

    setSubmitting(true);
    try {
      const res = await pointAPI.requestCharge({
        userId,
        bankCode: selectedBank,
        bankName,
        cost: amount,
      });
      if (res.success) {
        track('point-charge-completed', { bank: bankName, amount });
        localStorage.setItem('lastChargeBank', selectedBank);
        localStorage.setItem('lastChargeAmount', selectedAmount);
        showToast('충전 신청이 완료되었습니다.', 'success');
        setSelectedBank('');
        setSelectedAmount('');
        fetchChargeList();
        onChargeComplete?.();
      }
    } catch (err) {
      console.error('[PointCharge] Charge failed:', err);
      showToast('충전 신청에 실패했습니다.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!already?.paySeq) return;
    if (!confirm('충전 신청을 취소하시겠습니까?\n가상계좌 발급 후 취소 시에도 입금하면 정상 충전됩니다.')) return;

    setCancelling(true);
    try {
      const res = await pointAPI.cancelCharge({ userId, paySeq: already.paySeq });
      if (res.success) {
        showToast('취소되었습니다.', 'success');
        fetchChargeList();
        onChargeComplete?.();
      }
    } catch (err) {
      console.error('[PointCharge] Cancel failed:', err);
      showToast('취소에 실패했습니다.', 'error');
    } finally {
      setCancelling(false);
    }
  };

  if (!isOpen) return null;

  const parsedRemainCash = Number(remainCash?.toString().replace(/,/g, '') || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-tayo to-tayo-dark px-6 py-5 text-white relative overflow-hidden">
          <img
            src="/images/moon.png"
            alt=""
            onClick={handleMoonClick}
            className="absolute -right-2 -bottom-4 h-44 opacity-80 select-none cursor-pointer"
          />
          <div className="flex items-center justify-between relative z-10">
            <div>
              <h2 className="text-lg font-bold">포인트 충전</h2>
              <p className="text-blue-100 text-sm mt-1">가상계좌 입금 방식</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-4 bg-white/20 rounded-xl px-4 py-3">
            <p className="text-blue-100 text-xs">잔여 포인트</p>
            <p className="text-2xl font-bold tracking-tight">{parsedRemainCash.toLocaleString()}P</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-tayo-light border-t-tayo rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Pending charge notice */}
              {already && (
                <div className="bg-tayo-light border border-tayo/20 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-tayo rounded-full animate-pulse" />
                    <p className="text-sm font-semibold text-tayo-dark">충전 신청 진행 중</p>
                  </div>

                  <div className="bg-white rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">은행</span>
                      <span className="text-sm font-medium text-charcoal">{already.payBankName || ''}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">충전 금액</span>
                      <span className="text-sm font-bold text-tayo">{already.cost ? `${Number(already.cost).toLocaleString()}P` : ''}</span>
                    </div>
                    {already.payAddr && (
                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span className="text-xs text-sub-text">가상계좌</span>
                        <span className="text-sm font-mono font-bold text-charcoal">{already.payAddr}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-sub-text">신청일시</span>
                      <span className="text-xs text-sub-text">{already.regDate || ''}</span>
                    </div>
                  </div>

                  {!already.payAddr && (
                    <p className="text-xs text-tayo">
                      가상계좌 발급 중입니다. 5~10분 내에 문자로 안내됩니다.
                    </p>
                  )}
                  {already.payAddr && (
                    <p className="text-xs text-tayo">
                      위 가상계좌로 입금하시면 포인트가 충전됩니다.
                    </p>
                  )}

                  <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="w-full py-2 text-sm text-coral hover:text-coral hover:bg-coral/10 rounded-lg font-medium transition-colors"
                  >
                    {cancelling ? '취소 중...' : '충전 신청 취소'}
                  </button>
                </div>
              )}

              {/* Bank Selection */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">가상계좌 은행</label>
                <BottomSheetSelect
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  options={Object.entries(bankList).sort(([, a], [, b]) => a.localeCompare(b, 'ko')).map(([code, name]) => ({ value: code, label: name }))}
                  placeholder="은행을 선택하세요"
                  title="가상계좌 은행"
                  disabled={!!already}
                />
              </div>

              {/* Amount Selection */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">충전 금액</label>
                <BottomSheetSelect
                  value={selectedAmount}
                  onChange={(e) => setSelectedAmount(e.target.value)}
                  options={Object.entries(chargeOptions).map(([amount, label]) => ({ value: amount, label: label, sublabel: `${Number(amount).toLocaleString()}원` }))}
                  placeholder="금액을 선택하세요"
                  title="충전 금액"
                  disabled={!!already}
                />
              </div>

              {/* Summary */}
              {selectedBank && selectedAmount && (
                <div className="bg-tayo-light border border-tayo/20 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-sub-text">은행</span>
                    <span className="text-sm font-medium text-charcoal">{bankList[selectedBank]}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-sub-text">충전 포인트</span>
                    <span className="text-sm font-bold text-tayo">{Number(selectedAmount).toLocaleString()}P</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-tayo/20">
                    <span className="text-sm text-sub-text">입금할 금액</span>
                    <span className="text-base font-bold text-charcoal">{Number(selectedAmount).toLocaleString()}원</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleCharge}
                disabled={!selectedBank || !selectedAmount || submitting || !!already}
                className="w-full py-3.5 bg-tayo text-white rounded-xl font-medium hover:bg-tayo-dark disabled:bg-gray-200 disabled:text-sub-text disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    처리 중...
                  </span>
                ) : '충전 신청'}
              </button>

              <p className="text-xs text-sub-text text-center leading-relaxed">
                신청 후 5~10분 내에 가상계좌가 문자로 발송됩니다<br />
                발급된 가상계좌에 입금하면 포인트가 충전됩니다
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
